import {
  ClassificationGroup,
  Client,
  ChargeType,
} from '@core/generated/gql-types';
import { FeatureFlag, isFeatureEnabled } from './featureFlags';
import { getCustomAttribute } from './utils';

/** Charge types available when creating or editing a client. */
export const clientChargeTypeOptions: ChargeType[] = [
  ChargeType.Margin,
  ChargeType.Fixed,
  ChargeType.RateCard,
];

export const formatChargeTypeLabel = (chargeType: ChargeType): string => {
  switch (chargeType) {
    case ChargeType.Margin:
      return 'Margin';
    case ChargeType.Fixed:
      return 'Fixed';
    case ChargeType.RateCard:
      return 'Rate card';
    default:
      return String(chargeType);
  }
};

export const isMarginChargeType = (chargeType: ChargeType): boolean =>
  chargeType === ChargeType.Margin;

type ClientTab = {
  key: string;
  label: string;
  featureFlag?: FeatureFlag;
};

const allClientTabs: ClientTab[] = [
  { key: 'details', label: 'Details' },
  { key: 'sites', label: 'Sites', featureFlag: 'clientSitesTab' },
  { key: 'activity', label: 'Activity' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'candidates', label: 'Candidates' },
  { key: 'billing', label: 'Billing' },
  { key: 'rates', label: 'Rates' },
  {
    key: 'users',
    label: 'Users',
  },
  {
    key: 'documents',
    label: 'Documents',
  },
];

export type ClientTabKey = (typeof allClientTabs)[number]['key'];

export const clientTabs = allClientTabs.filter(
  (tab) => !tab.featureFlag || isFeatureEnabled(tab.featureFlag),
);

export const getClientIndustryDisplay = (
  client: Client,
  classificationGroups: ClassificationGroup[] = [],
): string => {
  const classifications = client.classifications ?? [];
  if (classifications.length === 0) {
    return getCustomAttribute(client, 'industry') || '-';
  }

  const groupNames = new Set<string>();
  for (const classification of classifications) {
    if (!classification) continue;
    const groupId = classification.classificationGroupId;
    if (groupId) {
      const groupName = classificationGroups.find(
        (group) => group.id === groupId,
      )?.name;
      if (groupName) {
        groupNames.add(groupName);
        continue;
      }
    }
    if (classification.name) {
      groupNames.add(classification.name);
    }
  }

  return groupNames.size > 0 ? Array.from(groupNames).join(', ') : '-';
};

export const getClientFields = (
  client: Client,
  classificationGroups: ClassificationGroup[] = [],
) => {
  const abn = getCustomAttribute(client, 'abn');
  const acn = getCustomAttribute(client, 'acn');
  const approvedBy = getCustomAttribute(client, 'approvedBy');
  const accountManager = getCustomAttribute(client, 'accountManager');
  const activated = getCustomAttribute(client, 'activated');

  const chargeType = client.chargeType || ChargeType.Margin;

  return [
    { label: 'Company Name', value: client.name },
    {
      label: 'Industry',
      value: getClientIndustryDisplay(client, classificationGroups),
    },
    {
      label: 'Address',
      value: client.address
        ? `${client.address.addressLine1 || ''}${client.address.addressLine2 ? ', ' + client.address.addressLine2 : ''}\n${client.address.suburb || ''}, ${client.address.state || ''} ${client.address.postcode || ''}, ${client.address.countryLabel || ''}`
        : '-',
    },
    { label: 'ABN', value: abn || '-' },
    {
      label: 'Margin Override Percentage',
      value:
        chargeType === ChargeType.Margin && client.marginOverridePercentage
          ? `${client.marginOverridePercentage * 100}%`
          : '-',
    },
    {
      label: 'Payment Term',
      value: client.paymentTerm ? `${client.paymentTerm} days` : '-',
    },
    { label: 'Charge Type', value: formatChargeTypeLabel(chargeType) },
    { label: 'ACN', value: acn || '-' },
    { label: 'Status', value: client.status },
    { label: 'Activated', value: activated || '-' },
    { label: 'Approved by', value: approvedBy || '-' },
    { label: 'Account Manager', value: accountManager || '-' },
  ];
};

export const getContactFields = (client: Client) => {
  const website = getCustomAttribute(client, 'website');
  const phoneNumber1 = getCustomAttribute(client, 'phoneNumber1');
  const email = getCustomAttribute(client, 'email');
  const phoneNumber2 = getCustomAttribute(client, 'phoneNumber2');
  return [
    { label: 'Email', value: email || '-' },
    { label: 'Phone Number 1', value: phoneNumber1 || '-' },
    { label: 'Phone Number 2', value: phoneNumber2 || '-' },
    { label: 'Website', value: website || '-' },
  ];
};
